import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, HostListener, OnDestroy, effect, inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { trigger, state, style, transition, animate } from '@angular/animations';

export interface QRModalConfig {
  appStoreUrl: string;
  playStoreUrl: string;
  qrCodeUrls: {
    appstore: string;
    playstore: string;
  };
}

@Component({
  selector: 'mzic-qr-download-modal',
  imports: [CommonModule, TranslocoModule],
  templateUrl: './qr-download-modal.component.html',
  styleUrl: './qr-download-modal.component.scss',
  animations: [
    trigger('modalOverlay', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('0.3s ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('0.3s ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('modalContent', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.8) translateY(30px)' }),
        animate('0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 0, transform: 'scale(0.8) translateY(30px)' }))
      ])
    ])
  ]
})
export class QRDownloadModalComponent implements OnDestroy {
  @Input() isVisible = false;
  @Input() activeStore: 'appstore' | 'playstore' | null = null;
  @Input() config!: QRModalConfig;
  @Input() translationScope = 'qrModal';

  @Output() closeModal = new EventEmitter<void>();
  @Output() directLinkClick = new EventEmitter<'appstore' | 'playstore'>();

  private translocoService = inject(TranslocoService);

  constructor() {
    // Handle body scroll when modal opens/closes
    effect(() => {
      if (this.isVisible) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    });
  }

  ngOnDestroy() {
    // Restore scrolling when component is destroyed
    document.body.style.overflow = 'auto';
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (this.isVisible) {
      this.onCloseModal();
    }
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  onModalBackdropClick() {
    this.onCloseModal();
  }

  onModalContentClick(event: Event) {
    event.stopPropagation();
  }

  getQRCodeUrl(): string {
    if (!this.activeStore || !this.config?.qrCodeUrls) return '';
    return this.config.qrCodeUrls[this.activeStore];
  }

  getStoreName(): string {
    if (!this.activeStore) return '';
    return this.activeStore === 'appstore' ? 'App Store' : 'Google Play';
  }

  getModalTitle(): string {
    if (!this.activeStore) return '';
    
    // Try to get localized title first
    const titleKey = this.activeStore === 'appstore' ? 'appStore' : 'googlePlay';
    const fullTranslationKey = `${this.translationScope}.title.${titleKey}`;
    
    try {
      const translatedTitle = this.translocoService.translate(fullTranslationKey);
      
      // Check if translation was found (if it returns the key itself, it wasn't found)
      if (translatedTitle && translatedTitle !== fullTranslationKey) {
        return translatedTitle;
      }
    } catch (error) {
      console.warn('Translation not found for:', fullTranslationKey);
    }
    
    // Fallback to English titles
    return this.activeStore === 'appstore' 
      ? 'Download from App Store' 
      : 'Download from Google Play';
  }

  onDirectLinkClick() {
    if (this.activeStore) {
      this.directLinkClick.emit(this.activeStore);
    }
  }

  onQRImageError(event: any) {
    console.error('QR Code image failed to load:', event);
    // You could emit an error event here if needed
  }
} 